import ClipboardJS from "clipboard";

type ClipboardRuntimeOptions = {
  successMessage?: string;
  errorMessage?: string;
  flashLabel?: boolean;
  flashSuccessLabel?: string;
  flashErrorLabel?: string;
  resetMs?: number;
};

declare global {
  interface Window {
    WMClipboard?: {
      readText: (trigger: Element | null) => string;
      normalizeText: (value: string, trigger?: Element | null) => string;
      showFeedback: (trigger: Element | null, ok: boolean, options?: ClipboardRuntimeOptions) => void;
    };
    __WMU_CLIPBOARD_BOOTED?: boolean;
  }
}

const SELECTOR = [
  "[data-clipboard-text]",
  "[data-copy-text]",
  "[data-copy-url]",
  "[data-copy-scan-url]",
  "[data-copy-firefly-url]",
  "[data-copy-handoff-url]",
  "[data-copy-target]",
].join(",");

function textFromTarget(selector: string): string {
  const target = document.querySelector(selector);
  if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
    return String(target.value || "").trim();
  }
  if (target instanceof HTMLElement) {
    return String(target.textContent || "").trim();
  }
  return "";
}

function resolveRawText(trigger: Element | null): string {
  if (!(trigger instanceof HTMLElement)) {
    return "";
  }
  const targetSelector = String(trigger.getAttribute("data-copy-target") || "").trim();
  if (targetSelector) {
    return textFromTarget(targetSelector);
  }
  const candidates = [
    "data-clipboard-text",
    "data-copy-text",
    "data-copy-url",
    "data-copy-scan-url",
    "data-copy-firefly-url",
  ];
  if (trigger.hasAttribute("data-copy-handoff-url")) {
    candidates.push("data-url");
  }
  for (const attr of candidates) {
    const value = String(trigger.getAttribute(attr) || "").trim();
    if (value) {
      return value;
    }
  }
  return "";
}

function shouldNormalizeAsURL(trigger: Element | null): boolean {
  if (!(trigger instanceof HTMLElement)) {
    return false;
  }
  return trigger.hasAttribute("data-copy-absolute")
    || trigger.hasAttribute("data-copy-url")
    || trigger.hasAttribute("data-copy-scan-url")
    || trigger.hasAttribute("data-copy-firefly-url")
    || trigger.hasAttribute("data-copy-handoff-url");
}

function normalizeText(value: string, trigger?: Element | null): string {
  const raw = String(value || "").trim();
  if (!raw) {
    return "";
  }
  if (!shouldNormalizeAsURL(trigger || null)) {
    return raw;
  }
  try {
    return new URL(raw, window.location.origin).toString();
  } catch (_error) {
    return raw;
  }
}

function successMessage(trigger: Element | null): string {
  if (!(trigger instanceof HTMLElement)) {
    return "Copied to clipboard.";
  }
  const explicit = String(trigger.getAttribute("data-copy-success") || "").trim();
  if (explicit) {
    return explicit;
  }
  if (trigger.hasAttribute("data-copy-scan-url") || trigger.hasAttribute("data-copy-url")) {
    return "Scan URL copied.";
  }
  if (trigger.hasAttribute("data-copy-firefly-url")) {
    return "Player URL copied.";
  }
  if (trigger.hasAttribute("data-copy-handoff-url")) {
    return "Handoff link copied.";
  }
  return "Copied to clipboard.";
}

function errorMessage(trigger: Element | null): string {
  if (!(trigger instanceof HTMLElement)) {
    return "Clipboard copy failed.";
  }
  const explicit = String(trigger.getAttribute("data-copy-error") || "").trim();
  if (explicit) {
    return explicit;
  }
  if (trigger.hasAttribute("data-copy-scan-url") || trigger.hasAttribute("data-copy-url")) {
    return "Could not copy scan URL.";
  }
  return "Clipboard copy failed.";
}

function flashLabel(trigger: Element | null, label: string, resetMs: number): void {
  if (!(trigger instanceof HTMLButtonElement)) {
    return;
  }
  if (!trigger.dataset.copyOriginalLabel) {
    trigger.dataset.copyOriginalLabel = trigger.textContent || "";
  }
  const original = trigger.dataset.copyOriginalLabel;
  trigger.textContent = label;
  window.setTimeout(() => {
    trigger.textContent = original;
  }, resetMs);
}

function feedbackOptions(trigger: Element | null): ClipboardRuntimeOptions {
  if (!(trigger instanceof HTMLElement)) {
    return {};
  }
  const resetMs = Number(trigger.getAttribute("data-copy-reset-ms") || "1400");
  return {
    successMessage: successMessage(trigger),
    errorMessage: errorMessage(trigger),
    flashLabel: trigger.getAttribute("data-copy-flash-label") !== "false",
    flashSuccessLabel: String(trigger.getAttribute("data-copy-label-success") || "Copied").trim() || "Copied",
    flashErrorLabel: String(trigger.getAttribute("data-copy-label-error") || "Copy failed").trim() || "Copy failed",
    resetMs: Number.isFinite(resetMs) && resetMs > 0 ? resetMs : 1400,
  };
}

function showFeedback(trigger: Element | null, ok: boolean, options?: ClipboardRuntimeOptions): void {
  const merged = {
    ...feedbackOptions(trigger),
    ...(options || {}),
  };
  if (merged.flashLabel) {
    flashLabel(trigger, ok ? (merged.flashSuccessLabel || "Copied") : (merged.flashErrorLabel || "Copy failed"), merged.resetMs || 1400);
  }
  const message = ok ? merged.successMessage : merged.errorMessage;
  if (message && typeof window.WMUToast === "function") {
    window.WMUToast(message, ok ? "success" : "warning");
  }
}

function dispatchClipboardEvent(type: "success" | "error", trigger: Element | null, text: string): void {
  document.dispatchEvent(new CustomEvent(`wmu:clipboard-${type}`, {
    detail: {
      trigger,
      text,
    },
  }));
}

function readText(trigger: Element | null): string {
  return normalizeText(resolveRawText(trigger), trigger);
}

function boot(): void {
  if (window.__WMU_CLIPBOARD_BOOTED) {
    return;
  }
  if (!(document.body instanceof HTMLElement)) {
    return;
  }
  window.__WMU_CLIPBOARD_BOOTED = true;
  window.WMClipboard = {
    readText,
    normalizeText,
    showFeedback,
  };

  const clipboard = new ClipboardJS(SELECTOR, {
    text: (trigger) => readText(trigger as Element | null),
  });

  clipboard.on("success", (event) => {
    const trigger = event.trigger as Element | null;
    const text = readText(trigger);
    showFeedback(trigger, true);
    dispatchClipboardEvent("success", trigger, text);
    event.clearSelection();
  });

  clipboard.on("error", (event) => {
    const trigger = event.trigger as Element | null;
    const text = readText(trigger);
    showFeedback(trigger, false);
    dispatchClipboardEvent("error", trigger, text);
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", boot);
} else {
  boot();
}
